import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Star, ChevronRight, ChevronLeft } from "lucide-react";

/**
 * PublicReviews — Iteration 30
 * Renders ONLY admin-approved reviews on the public landing page. Hidden
 * entirely when there are no approved reviews yet so the page never shows
 * an empty section.
 *
 * Presentation: responsive horizontal carousel.
 * - Desktop (lg): 3 cards visible, arrow nav + dots.
 * - Tablet (sm): 2 cards visible.
 * - Mobile: peek carousel — active card ~84%, previous/next edges peek.
 * Data, ordering, moderation and card content are unchanged.
 */
const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("ar-SA", {
      year: "numeric",
      month: "long",
    });
  } catch {
    return "";
  }
};

const Stars = ({ value }) => (
  <div className="flex gap-0.5" aria-label={`${value} من 5 نجوم`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        className={`w-4 h-4 ${
          n <= value ? "fill-[#C5A059] text-[#C5A059]" : "text-[#D9D5CC]"
        }`}
      />
    ))}
  </div>
);

const PublicReviews = () => {
  const [items, setItems] = useState(null); // null = loading
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef(null);
  const cardRefs = useRef([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/reviews/public");
        if (cancelled) return;
        setItems(data?.items || []);
      } catch {
        if (cancelled) return;
        setItems([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const updateActiveFromScroll = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;
    const containerRect = container.getBoundingClientRect();
    const center = containerRect.left + containerRect.width / 2;
    let closest = 0;
    let minDist = Infinity;
    cardRefs.current.forEach((el, i) => {
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const cardCenter = rect.left + rect.width / 2;
      const dist = Math.abs(center - cardCenter);
      if (dist < minDist) {
        minDist = dist;
        closest = i;
      }
    });
    setActiveIndex((prev) => (prev === closest ? prev : closest));
  }, []);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    let rafId = null;
    const onScroll = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        updateActiveFromScroll();
      });
    };
    container.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      container.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, [updateActiveFromScroll, items]);

  const scrollToIndex = (i) => {
    const target = cardRefs.current[i];
    if (!target) return;
    target.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  };

  const goPrev = () => {
    if (!items || items.length === 0) return;
    const next = Math.max(0, activeIndex - 1);
    scrollToIndex(next);
  };
  const goNext = () => {
    if (!items || items.length === 0) return;
    const next = Math.min(items.length - 1, activeIndex + 1);
    scrollToIndex(next);
  };

  if (items === null) return null;
  if (items.length === 0) return null;

  const count = items.length;

  return (
    <section
      className="max-w-7xl mx-auto px-6 lg:px-12 mt-24"
      data-testid="public-reviews-section"
      dir="rtl"
    >
      <div className="text-center max-w-2xl mx-auto">
        <span className="text-[#0A4D3C] tracking-widest text-sm">آراء العملاء</span>
        <h2 className="font-heading text-3xl sm:text-4xl text-[#222222] mt-2">
          ماذا قالوا بعد التجربة
        </h2>
        <p className="mt-3 text-[#5E5C58] leading-loose">
          آراء حقيقية من عملاء أتمّوا جلستهم الاستشارية أو حصلوا على الكتاب الرقمي.
        </p>
      </div>

      <div className="relative mt-10">
        {/* Desktop / tablet navigation arrows */}
        <button
          type="button"
          onClick={goPrev}
          disabled={activeIndex === 0}
          aria-label="المراجعة السابقة"
          data-testid="public-reviews-prev"
          className="hidden sm:flex items-center justify-center absolute top-1/2 -translate-y-1/2 right-[-8px] lg:right-[-20px] z-10 w-10 h-10 rounded-full bg-white border border-[#E5E1D8] shadow-[0_2px_18px_-10px_rgba(60,40,20,0.25)] text-[#0A4D3C] hover:bg-[#F7F3EA] disabled:opacity-40 disabled:cursor-not-allowed transition"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
        <button
          type="button"
          onClick={goNext}
          disabled={activeIndex >= count - 1}
          aria-label="المراجعة التالية"
          data-testid="public-reviews-next"
          className="hidden sm:flex items-center justify-center absolute top-1/2 -translate-y-1/2 left-[-8px] lg:left-[-20px] z-10 w-10 h-10 rounded-full bg-white border border-[#E5E1D8] shadow-[0_2px_18px_-10px_rgba(60,40,20,0.25)] text-[#0A4D3C] hover:bg-[#F7F3EA] disabled:opacity-40 disabled:cursor-not-allowed transition"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        <div
          ref={scrollRef}
          data-testid="public-reviews-carousel"
          className="reviews-carousel-scroll flex gap-4 sm:gap-5 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 -mx-6 px-[9vw] sm:mx-0 sm:px-1"
          role="region"
          aria-roledescription="carousel"
          aria-label="آراء العملاء"
        >
          {items.map((r, idx) => (
            <article
              key={r.id}
              ref={(el) => { cardRefs.current[idx] = el; }}
              data-testid={`public-review-${r.id}`}
              aria-roledescription="slide"
              aria-label={`${idx + 1} من ${count}`}
              className="flex-none snap-center sm:snap-start
                         w-[82vw] sm:w-[calc(50%-10px)] lg:w-[calc(33.3333%-14px)]
                         rounded-2xl border border-[#E5E1D8] bg-white p-6
                         shadow-[0_2px_24px_-18px_rgba(60,40,20,0.25)]"
            >
              <Stars value={r.rating} />
              {r.comment && (
                <p className="mt-4 text-[#222222] leading-relaxed text-sm">
                  «{r.comment}»
                </p>
              )}
              <div className="mt-4 pt-3 border-t border-[#F0EDE4] flex items-center justify-between text-xs text-[#5E5C58]">
                <span className="font-medium text-[#222222]">
                  {r.display_name || "عميل/ة"}
                </span>
                <span>{formatDate(r.moderated_at || r.created_at)}</span>
              </div>
            </article>
          ))}
        </div>
      </div>

      {/* Pagination dots */}
      {count > 1 && (
        <div
          className="mt-5 flex items-center justify-center gap-2"
          data-testid="public-reviews-dots"
          aria-hidden="true"
        >
          {items.map((r, idx) => (
            <button
              key={r.id}
              type="button"
              tabIndex={-1}
              onClick={() => scrollToIndex(idx)}
              className={`h-2 rounded-full transition-all ${
                idx === activeIndex
                  ? "w-6 bg-[#0A4D3C]"
                  : "w-2 bg-[#D9D5CC] hover:bg-[#B9B0A0]"
              }`}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default PublicReviews;
